import { createClient } from '@supabase/supabase-js';
import logoUrl from './assets/qualysports-logo.png';
import './super6.js';
import './supers.js';

const app = document.querySelector('#app');
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const initialUrl = new URL(window.location.href);
const recoveryRedirect = initialUrl.searchParams.get('password-recovery') === '1'
  || new URLSearchParams(initialUrl.hash.slice(1)).get('type') === 'recovery';
const checkoutResult = initialUrl.searchParams.get('checkout');
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

const FORMATS = [
  { id: 'super-6', count: '06', name: 'Super 6' },
  { id: 'super-8', count: '08', name: 'Super 8' },
  { id: 'super-8-fixa', count: '08', name: 'Super 8 · dupla fixa' },
  { id: 'super-10-normal', count: '10', name: 'Super 10' },
  { id: 'super-10', count: '10', name: 'Super 10 dois grupos' },
  { id: 'super-12', count: '12', name: 'Super 12' },
  { id: 'super-16', count: '16', name: 'Super 16' },
];

const icons = {
  plus: '<path d="M12 5v14M5 12h14"/>',
  arrow: '<path d="M5 12h14m-6-6 6 6-6 6"/>',
  back: '<path d="m14 6-6 6 6 6"/>',
  calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v4m10-4v4M3 10h18"/>',
  grid: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  trophy: '<path d="M7 4h10v7a5 5 0 0 1-10 0V4Zm0 2H4v3a4 4 0 0 0 4 4m9-7h3v3a4 4 0 0 1-4 4m-4 3v4m-4 0h8"/>',
  logout: '<path d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5m4-4 4-4-4-4m4 4H9"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5m0-8h.01"/>',
  eye: '<path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.5"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
  download: '<path d="M12 3v12m-5-5 5 5 5-5M5 21h14"/>',
  trash: '<path d="M4 7h16M9 7V4h6v3m3 0-1 14H7L6 7m4 4v6m4-6v6"/>',
  restore: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8m0-5v5h5"/>',
  flag: '<path d="M5 21V4m0 1h10l-1 4 3 4H5"/>',
  wallet: '<path d="M4 6h15a2 2 0 0 1 2 2v10H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h12"/><path d="M15 11h6v4h-6a2 2 0 1 1 0-4Z"/>',
  gift: '<rect x="3" y="8" width="18" height="13" rx="2"/><path d="M12 8v13M3 12h18M7.5 8C5 8 4 6.7 4 5.4 4 4.1 5 3 6.3 3 8.7 3 12 8 12 8m4.5 0C19 8 20 6.7 20 5.4 20 4.1 19 3 17.7 3 15.3 3 12 8 12 8"/>',
};
const icon = (name, extra = '') => `<svg class="icon ${extra}" viewBox="0 0 24 24" aria-hidden="true">${icons[name]}</svg>`;
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const formatDate = (date) => date ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`)) : '—';
const formatAccountDate = (date) => date ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(date)) : 'Data indisponível';
const formatDateTime = (date) => date ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(date)) : 'Data indisponível';
const formatGameAverage = (value) => Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatCurrency = (cents) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(cents || 0) / 100);
const formatCurrencyInput = (cents) => (Number(cents || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function formatElapsed(startedAt, now = Date.now()) {
  const started = Date.parse(startedAt || '');
  if (!Number.isFinite(started)) return '00:00';
  const totalSeconds = Math.max(0, Math.floor((now - started) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
function parseCurrencyToCents(value) {
  const normalized = String(value ?? '').trim().replace(/\s/g, '').replace(/^R\$/i, '');
  if (!normalized) return null;
  const decimal = normalized.includes(',') ? normalized.replace(/\./g, '').replace(',', '.') : normalized;
  const amount = Number(decimal);
  const cents = Math.round(amount * 100);
  return Number.isFinite(amount) && amount >= 0 && Number.isSafeInteger(cents) ? cents : null;
}
const firstName = (name) => name.trim().split(/\s+/)[0];
const initials = (name) => name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
const state = { authMode: recoveryRedirect ? 'reset' : 'login', passwordRecovery: recoveryRedirect, page: 'home', selectedId: null, detailTab: 'config', scorePickerTrigger: null, collapsedRounds: new Set(), showDeleted: false, user: null, authUser: null, admin: null, impersonatingId: null, tournamentItems: [], adminTournaments: [], users: [], creditBalance: 0, creditTransactions: [], creditLoading: false, loading: true, configError: !supabase, toastTimer: null, publicToken: null, publicItem: null, publicTab: 'games', publicError: null, publicUpdatedAt: null };
let publicRefreshTimer = null;
const isAdminSession = () => state.admin?.role === 'admin';
const isImpersonating = () => isAdminSession() && !!state.impersonatingId;
const currentFormatName = (format, fallback = '') => FORMATS.find((entry) => entry.id === format)?.name || fallback;

function fromTournamentRow(row) {
  return { id: row.id, userId: row.user_id, name: row.name, date: row.date, format: row.format, formatName: currentFormatName(row.format, row.format_name), scoring: row.scoring, athletes: row.athletes || [], scores: row.scores || [], matchStates: Array.isArray(row.match_states) ? row.match_states : [], finalSeed: row.final_seed, drawn: row.drawn === true, registrationFeeCents: Number(row.registration_fee_cents || 0), payments: Array.isArray(row.payments) ? row.payments : [], shareToken: row.share_token || null, shareEnabled: row.share_enabled === true, deletedAt: row.deleted_at || null, createdAt: row.created_at, updatedAt: row.updated_at };
}
function tournamentValues(item) {
  return { user_id: item.userId, name: item.name, date: item.date, format: item.format, format_name: item.formatName, scoring: item.scoring || null, athletes: item.athletes || [], scores: item.scores || [], match_states: item.matchStates || [], final_seed: item.finalSeed || null, drawn: item.drawn === true, registration_fee_cents: Number(item.registrationFeeCents || 0), payments: item.payments || [], share_token: item.shareToken || null, share_enabled: item.shareEnabled === true, deleted_at: item.deletedAt || null, updated_at: new Date().toISOString() };
}

function sharedTokenFromHash() {
  return window.location.hash.match(/^#\/acompanhar\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i)?.[1] || null;
}
function passwordRecoveryRedirectUrl() {
  const url = new URL(window.location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set('password-recovery', '1');
  return url.toString();
}
function clearPasswordRecoveryUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('password-recovery');
  url.hash = '';
  window.history.replaceState({}, '', `${url.pathname}${url.search}`);
}
function clearCheckoutReturnUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('checkout');
  url.searchParams.delete('session_id');
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}
function fromSharedTournament(item) {
  return { id: item.id, name: item.name, date: item.date, format: item.format, formatName: currentFormatName(item.format, item.formatName), scoring: item.scoring, athletes: Array.isArray(item.athletes) ? item.athletes : [], scores: Array.isArray(item.scores) ? item.scores : [], matchStates: Array.isArray(item.matchStates) ? item.matchStates : [], finalSeed: item.finalSeed || null, drawn: item.drawn === true, updatedAt: item.updatedAt || null };
}
async function loadSharedTournament(token, silent = false) {
  if (!supabase) { state.loading = false; state.publicError = 'A página pública está temporariamente indisponível.'; render(); return; }
  try {
    const { data, error } = await supabase.rpc('get_shared_tournament', { p_token: token });
    if (error) throw error;
    state.publicItem = data ? fromSharedTournament(data) : null;
    state.publicError = data ? null : 'Este link não está mais disponível.';
    state.publicUpdatedAt = data ? new Date() : null;
  } catch (error) {
    console.error(error);
    if (!silent || !state.publicItem) state.publicError = 'Não foi possível carregar este Super agora.';
  } finally {
    state.loading = false;
    render(true);
  }
}
function startPublicRefresh(token) {
  clearInterval(publicRefreshTimer);
  publicRefreshTimer = window.setInterval(() => loadSharedTournament(token, true), 5000);
}
async function initializeApp() {
  const token = sharedTokenFromHash();
  if (window.location.hash.startsWith('#/acompanhar/')) {
    state.publicToken = token || 'invalid';
    if (!token) {
      state.loading = false;
      state.publicError = 'O endereço deste link não é válido.';
      render();
      return;
    }
    await loadSharedTournament(token);
    startPublicRefresh(token);
    return;
  }
  await loadSession();
}
function profileFromAuth(authUser, profile = {}) {
  return { id: authUser.id, name: profile.name || authUser.user_metadata?.name || authUser.email?.split('@')[0] || 'Usuário', email: profile.email || authUser.email || '', role: authUser.app_metadata?.role === 'admin' ? 'admin' : 'user', creditBalance: Number(profile.credit_balance || 0), createdAt: profile.created_at || authUser.created_at };
}
function fromCreditTransaction(row) {
  return { id: row.id, type: row.transaction_type, amount: Number(row.amount || 0), balanceAfter: Number(row.balance_after || 0), description: row.description || '', createdAt: row.created_at };
}
async function loadCreditData(userId) {
  state.creditLoading = true;
  const [{ data: profile, error: profileError }, { data: transactions, error: transactionsError }] = await Promise.all([
    supabase.from('profiles').select('credit_balance').eq('id', userId).single(),
    supabase.from('credit_transactions').select('id,transaction_type,amount,balance_after,description,created_at').eq('user_id', userId).order('created_at', { ascending: false }).order('id', { ascending: false }).limit(50),
  ]);
  state.creditLoading = false;
  if (profileError) throw profileError;
  if (transactionsError) throw transactionsError;
  state.creditBalance = Number(profile?.credit_balance || 0);
  state.creditTransactions = (transactions || []).map(fromCreditTransaction);
  if (state.user?.id === userId) state.user.creditBalance = state.creditBalance;
}
async function fetchTournaments(userId) {
  const { data, error } = await supabase.from('tournaments').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  if (error) throw error;
  state.tournamentItems = (data || []).map(fromTournamentRow);
}
async function loadAdminData() {
  const [{ data: users, error: usersError }, { data: items, error: itemsError }] = await Promise.all([
    supabase.from('profiles').select('id,name,email,credit_balance,created_at').order('created_at', { ascending: false }),
    supabase.from('tournaments').select('*').order('created_at', { ascending: false }),
  ]);
  if (usersError) throw usersError;
  if (itemsError) throw itemsError;
  state.users = (users || []).filter((user) => user.id !== state.admin.id).map((user) => ({ ...user, creditBalance: Number(user.credit_balance || 0), createdAt: user.created_at }));
  state.adminTournaments = (items || []).map(fromTournamentRow);
  state.tournamentItems = state.adminTournaments.filter((item) => item.userId === state.user.id);
}
async function establishSession(authUser) {
  const { data: profile, error } = await supabase.from('profiles').select('id,name,email,credit_balance,created_at').eq('id', authUser.id).single();
  if (error) throw error;
  const user = profileFromAuth(authUser, profile);
  state.authUser = authUser;
  state.admin = user.role === 'admin' ? user : null;
  state.user = user;
  state.impersonatingId = null;
  state.selectedId = null;
  state.page = user.role === 'admin' ? 'users' : 'home';
  if (state.admin) {
    await loadAdminData();
    await loadCreditData(user.id);
  } else {
    await Promise.all([fetchTournaments(user.id), loadCreditData(user.id)]);
  }
  if (checkoutResult === 'success' || checkoutResult === 'cancelled') {
    state.page = 'credits';
    clearCheckoutReturnUrl();
    queueMicrotask(() => showToast(checkoutResult === 'success' ? 'Pagamento concluído. Atualizando seu saldo de créditos.' : 'Pagamento cancelado. Nenhuma cobrança foi realizada.', checkoutResult !== 'success'));
    if (checkoutResult === 'success') {
      window.setTimeout(async () => {
        try { await loadCreditData(user.id); render(true); } catch (error) { console.error(error); }
      }, 2500);
    }
  }
}
async function loadSession() {
  if (!supabase) { state.loading = false; render(); return; }
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    if (state.passwordRecovery) {
      if (!session?.user) throw new Error('Este link de recuperação é inválido ou expirou. Solicite um novo link.');
      state.authUser = session.user;
      state.user = null;
      state.authMode = 'reset';
    } else if (session?.user) await establishSession(session.user);
  } catch (error) {
    console.error(error);
    state.user = null;
    state.authUser = null;
    if (state.passwordRecovery) {
      state.passwordRecovery = false;
      state.authMode = 'login';
      clearPasswordRecoveryUrl();
      queueMicrotask(() => showToast('Este link de recuperação é inválido ou expirou. Solicite um novo link.', true));
    }
  } finally {
    state.loading = false;
    render();
  }
}
function tournaments() { return state.tournamentItems; }
async function saveTournament(item) {
  const { data, error } = await supabase.from('tournaments').update(tournamentValues(item)).eq('id', item.id).select().single();
  if (error) throw error;
  const saved = fromTournamentRow(data);
  state.tournamentItems = state.tournamentItems.map((entry) => entry.id === saved.id ? saved : entry);
  if (isAdminSession()) state.adminTournaments = state.adminTournaments.map((entry) => entry.id === saved.id ? saved : entry);
  return saved;
}
function tournamentProgress(item) {
  const data = Supers.format(item.format);
  const total = data?.phases ? 20 : data?.games.length || 0;
  if (!data || !Super6.validConfig(item.scoring) || !Supers.athletesReady(item) || !item.drawn) return { completed: 0, total, finished: false };
  const completed = data.phases
    ? (item.scores || []).filter((score) => Super6.isComplete(score, item.scoring)).length
    : Supers.standings(item).completed;
  return { completed, total, finished: total > 0 && completed === total };
}
function tournamentStatus(item) {
  const data = Supers.format(item.format);
  if (!data) return 'Formato indisponível';
  if (!Super6.validConfig(item.scoring)) return 'Configure o placar';
  if (!Supers.athletesReady(item)) return data.fixed ? 'Cadastre as duplas' : 'Cadastre os atletas';
  if (!item.drawn) return 'Aguardando sorteio';
  const { completed, total, finished } = tournamentProgress(item);
  return finished ? 'Finalizado' : `${completed}/${total} jogos lançados`;
}
function brand() { return `<div class="brand" aria-label="Qualysports Supers"><span class="brand-image-clip"><img src="${logoUrl}" alt="" /></span><span class="brand-name">Qualysports<small>Supers</small></span></div>`; }

function field(id, label, type, placeholder, options = '') {
  return `<div class="form-field"><label for="${id}">${label}</label><input id="${id}" name="${id}" type="${type}" placeholder="${placeholder}" ${options} /></div>`;
}
function passwordField(id, label, autocomplete) {
  return `<div class="form-field"><label for="${id}">${label}</label><div class="password-wrap"><input id="${id}" name="${id}" type="password" placeholder="Mínimo de 8 caracteres" autocomplete="${autocomplete}" minlength="8" required /><button class="show-password" type="button" data-action="toggle-password" data-target="${id}" aria-label="Mostrar senha">${icon('eye')}</button></div></div>`;
}

function authView() {
  const register = state.authMode === 'register';
  const forgot = state.authMode === 'forgot';
  const reset = state.authMode === 'reset';
  const title = register ? 'Crie sua conta' : forgot ? 'Recuperar senha' : reset ? 'Criar nova senha' : 'Bem-vindo';
  const lead = register ? 'Comece a organizar os Supers da sua academia.' : forgot ? 'Informe seu e-mail para receber o link de recuperação.' : reset ? 'Escolha uma nova senha para acessar sua conta.' : '';
  const form = forgot
    ? `<form id="password-recovery-request-form">${field('recovery-email', 'E-mail', 'email', 'seuemail@exemplo.com', 'autocomplete="email" required')}<button class="btn btn-primary full auth-submit" type="submit">Enviar link</button></form>`
    : reset
      ? `<form id="password-reset-form">${passwordField('new-password', 'Nova senha', 'new-password')}${passwordField('new-password-confirm', 'Confirmar nova senha', 'new-password')}<button class="btn btn-primary full auth-submit" type="submit">Salvar nova senha</button></form>`
      : `<form id="auth-form" data-mode="${register ? 'register' : 'login'}">
          ${register ? field('name', 'Seu nome', 'text', 'Como podemos chamar você?', 'autocomplete="name" maxlength="80" required') : ''}
          ${field('email', 'E-mail', 'email', 'seuemail@exemplo.com', 'autocomplete="email" required')}
          ${passwordField('password', 'Senha', register ? 'new-password' : 'current-password')}
          ${register ? passwordField('confirm', 'Confirmar senha', 'new-password') : '<div class="auth-help"><button class="btn-text" type="button" data-action="auth-mode" data-mode="forgot">Esqueci minha senha</button></div>'}
          <button class="btn btn-primary full auth-submit" type="submit">${register ? `Criar conta e começar ${icon('arrow')}` : 'Entrar'}</button>
        </form>`;
  return `<div class="auth-layout">
    <section class="auth-visual" aria-label="Apresentação do Supers">
      ${brand()}
      <div class="auth-copy"><span class="eyebrow">ORGANIZAÇÃO DE TORNEIOS</span><h1>Seus Supers,<br />em um só lugar.</h1><p>Super 6, 8, 10, 12</p></div>
    </section>
    <main class="auth-main"><div class="auth-panel"><h2>${title}</h2>${lead ? `<p class="lead">${lead}</p>` : ''}
        ${form}
        <p class="auth-switch"><button class="btn-text" type="button" data-action="auth-mode" data-mode="${register || forgot || reset ? 'login' : 'register'}">${register || forgot || reset ? 'Voltar para o login' : 'Criar minha conta'}</button></p>
      </div>
    </main>
  </div>`;
}

function shell(content, title, hideTopbar = false) {
  const name = escapeHtml(state.user.name);
  const email = escapeHtml(state.user.email);
  const adminNav = isAdminSession() ? `<button class="nav-item ${state.page === 'users' ? 'active' : ''}" type="button" data-action="users">${icon('users')}<span>Usuários</span></button>` : '';
  const creditsNav = !isAdminSession() || isImpersonating() ? `<button class="nav-item ${state.page === 'credits' ? 'active' : ''}" type="button" data-action="credits">${icon('wallet')}<span>Créditos</span><b class="nav-badge">${state.creditBalance}</b></button>` : '';
  const impersonationBar = isImpersonating() ? `<div class="impersonation-bar" role="status"><div>${icon('eye')}<span>Visualizando a conta de <strong>${name}</strong></span></div><button type="button" data-action="admin-return">Voltar ao administrador ${icon('back')}</button></div>` : '';
  return `<div class="shell"><aside class="sidebar">
    ${brand()}<nav aria-label="Navegação principal"><button class="nav-item ${state.page === 'home' ? 'active' : ''}" type="button" data-action="home">${icon('grid')}<span>Visão geral</span></button><button class="nav-item ${state.page === 'create' ? 'active' : ''}" type="button" data-action="create">${icon('plus')}<span>Novo Super</span></button>${creditsNav}${adminNav}</nav>
    <div class="sidebar-spacer"></div>
    <div class="sidebar-user"><div class="avatar">${escapeHtml(initials(state.user.name))}</div><div class="who"><strong>${name}</strong><small>${email}</small></div><button class="signout" type="button" data-action="logout" aria-label="Sair">${icon('logout')}</button></div>
    <button class="signout mobile-signout" style="display:none" type="button" data-action="logout" aria-label="Sair">${icon('logout')}</button>
  </aside><div class="main-area">${hideTopbar ? '' : `<header class="topbar"><div class="breadcrumbs"><span>Supers</span><span>›</span><b>${title}</b></div><div class="topbar-right"><div class="avatar">${escapeHtml(initials(state.user.name))}</div></div></header>`}${impersonationBar}<main class="content">${content}</main></div></div>`;
}

function homeView() {
  const items = tournaments();
  const deleted = items.filter((item) => item.deletedAt);
  const available = items.filter((item) => !item.deletedAt);
  const finished = available.filter((item) => tournamentProgress(item).finished);
  const active = available.filter((item) => !tournamentProgress(item).finished);
  const cards = (list, final = false) => list.map((item) => `<button class="tournament-card${final ? ' finished' : ''}" type="button" data-action="detail" data-id="${escapeHtml(item.id)}"><div class="tournament-card-top"><span class="format-badge">${escapeHtml(item.formatName)}</span>${icon('arrow')}</div><h3>${escapeHtml(item.name)}</h3><div class="card-foot">${icon('calendar')} ${formatDate(item.date)} <span>·</span> ${escapeHtml(tournamentStatus(item))}</div></button>`).join('');
  const group = (title, list, final = false) => list.length ? `<section class="tournament-section"><div class="section-title"><h2>${title}</h2></div><div class="tournament-grid">${cards(list, final)}</div></section>` : '';
  const deletedCards = deleted.map((item) => `<article class="tournament-card deleted"><div class="tournament-card-top"><span class="format-badge">${escapeHtml(item.formatName)}</span><span class="deleted-label">Excluído</span></div><h3>${escapeHtml(item.name)}</h3><div class="card-foot">${icon('calendar')} ${formatDate(item.date)}</div><button class="btn btn-restore" type="button" data-action="restore-tournament" data-id="${escapeHtml(item.id)}">${icon('restore')} Restaurar</button></article>`).join('');
  const deletedSection = `<section class="tournament-section deleted-section"><button class="deleted-toggle" type="button" data-action="toggle-deleted" aria-expanded="${state.showDeleted}"><span>Excluídos</span><span><b>${deleted.length}</b> ${state.showDeleted ? '−' : '+'}</span></button>${state.showDeleted ? (deleted.length ? `<div class="tournament-grid deleted-grid">${deletedCards}</div>` : '<p class="deleted-empty">Nenhum torneio excluído.</p>') : ''}</section>`;
  return shell(`<div class="page-head"><div><h1>Olá, ${escapeHtml(firstName(state.user.name))}</h1></div><button class="btn btn-primary" type="button" data-action="create">${icon('plus')} Novo Super</button></div>
    <div class="overview"><div class="summary-stat"><strong>${active.length}</strong><span>Em andamento</span></div><div class="summary-stat"><strong>${finished.length}</strong><span>Finalizados</span></div><button class="credit-stat" type="button" data-action="credits"><div class="credit-stat-icon">${icon('wallet')}</div><div><strong>${state.creditBalance}</strong><span>${state.creditBalance === 1 ? 'Crédito disponível' : 'Créditos disponíveis'}</span></div></button></div>
    ${available.length ? `${group('Em andamento', active)}${group('Finalizados', finished, true)}` : ''}${deletedSection}`, 'Visão geral', true);
}

function usersView() {
  const users = state.users;
  const allSupers = state.adminTournaments;
  const cards = users.map((user) => {
    const userSupers = allSupers.filter((item) => item.userId === user.id);
    const deletedCount = userSupers.filter((item) => item.deletedAt).length;
    const activeCount = userSupers.length - deletedCount;
    return `<article class="user-card"><div class="user-card-main"><div class="avatar">${escapeHtml(initials(user.name))}</div><div><h2>${escapeHtml(user.name)}</h2><p>${escapeHtml(user.email)}</p></div></div><dl><div><dt>Cadastro</dt><dd>${escapeHtml(formatAccountDate(user.createdAt))}</dd></div><div><dt>Créditos</dt><dd>${user.creditBalance}</dd></div><div><dt>Ativos</dt><dd>${activeCount}</dd></div><div><dt>Excluídos</dt><dd>${deletedCount}</dd></div></dl><div class="user-card-actions"><button class="btn btn-ghost" type="button" data-action="admin-credit-open" data-user-id="${escapeHtml(user.id)}">${icon('gift')} Adicionar créditos</button><button class="btn btn-primary" type="button" data-action="impersonate" data-user-id="${escapeHtml(user.id)}">Abrir painel</button></div></article>`;
  }).join('');
  return shell(`<div class="page-head admin-page-head"><div><span class="eyebrow">Administração</span><h1>Usuários cadastrados</h1></div><span class="admin-total">${users.length} ${users.length === 1 ? 'usuário' : 'usuários'}</span></div>${users.length ? `<section class="users-grid" aria-label="Cadastros de usuários">${cards}</section>` : `<div class="empty-state"><div class="empty-icon">${icon('users')}</div><h3>Nenhum usuário cadastrado</h3><p>Os novos cadastros aparecerão aqui automaticamente.</p></div>`}`, 'Usuários');
}

function createView() {
  const choices = FORMATS.map((format) => `<option value="${format.id}">${format.name}</option>`).join('');
  if (state.creditBalance <= 0) {
    const action = isImpersonating() ? `<button class="btn btn-primary" type="button" data-action="admin-return">Voltar aos usuários</button>` : `<button class="btn btn-primary" type="button" data-action="credits">Comprar créditos</button>`;
    return shell(`<button class="back-link" type="button" data-action="home">${icon('back')} Voltar para meus Supers</button><div class="page-head"><div><h1>Criar novo Super</h1></div></div><div class="credit-empty-state"><div class="empty-icon">${icon('wallet')}</div><h2>Você precisa de 1 crédito</h2><p>Cada crédito permite criar um torneio. Excluir um torneio depois não devolve o crédito utilizado.</p>${action}</div>`, 'Novo Super', true);
  }
  return shell(`<button class="back-link" type="button" data-action="home">${icon('back')} Voltar para meus Supers</button><div class="page-head"><div><h1>Criar novo Super</h1></div></div>
    <div class="credit-create-notice">${icon('wallet')}<span>Este torneio utilizará <strong>1 crédito</strong>. Seu saldo ficará em <strong>${state.creditBalance - 1}</strong>.</span></div><div class="create-layout create-only"><form class="form-card" id="create-form">
      <div class="form-field"><label for="format">Tipo de Super</label><select id="format" name="format" required><option value="" selected disabled>Selecione o tipo de Super</option>${choices}</select></div>
      <div class="divider"></div>
      ${field('tournament-name', 'Nome do torneio', 'text', 'Ex.: Super de Primavera', 'maxlength="100" required')}
      ${field('tournament-date', 'Data do torneio', 'date', '', `value="${today()}" required`)}
      <div class="form-actions"><button class="btn btn-ghost" type="button" data-action="home">Cancelar</button><button class="btn btn-primary" type="submit">Criar Super</button></div>
    </form></div>`, 'Novo Super', true);
}

function creditsView() {
  const packages = [
    { credits: 5, price: 'R$ 50,00', perCredit: 'R$ 10,00 por crédito' },
    { credits: 10, price: 'R$ 90,00', perCredit: 'R$ 9,00 por crédito', featured: true },
  ];
  const packageCards = packages.map((entry) => `<article class="credit-package${entry.featured ? ' featured' : ''}"><div class="credit-package-head"><h2>${entry.credits} créditos</h2>${entry.featured ? '<span class="credit-package-badge">Melhor valor</span>' : ''}</div><strong>${entry.price}</strong><p>${entry.perCredit}</p><button class="btn btn-primary" type="button" data-action="buy-credits" data-credits="${entry.credits}">Comprar pacote</button></article>`).join('');
  const labels = { purchase: 'Compra aprovada', admin_grant: 'Crédito concedido pelo administrador', tournament_creation: 'Criação de torneio', payment_reversal: 'Estorno do pagamento' };
  const history = state.creditTransactions.length ? state.creditTransactions.map((entry) => `<li class="credit-row"><div><strong>${escapeHtml(entry.description || labels[entry.type] || 'Movimentação de créditos')}</strong><small>${formatDateTime(entry.createdAt)} · Saldo após: ${entry.balanceAfter}</small></div><b class="${entry.amount > 0 ? 'positive' : 'negative'}">${entry.amount > 0 ? '+' : ''}${entry.amount}</b></li>`).join('') : '<li class="credit-history-empty">Nenhuma movimentação de créditos ainda.</li>';
  const purchasing = isImpersonating() ? '<div class="credit-admin-note">Você está visualizando este usuário. Para adicionar créditos, volte ao painel administrativo.</div>' : `<section class="credit-purchase-section"><div class="credit-packages">${packageCards}</div><p class="credit-terms">Os créditos não expiram, não podem ser transferidos e não são convertidos em dinheiro.</p></section>`;
  return shell(`<button class="back-link" type="button" data-action="home">${icon('back')} Voltar para meus Supers</button><div class="page-head credit-page-head"><div><h1>Créditos</h1></div></div><div class="credit-balance-row"><section class="credit-balance-card"><div class="credit-balance-icon">${icon('wallet')}</div><div><span>Saldo disponível</span><strong>${state.creditBalance}</strong><p>${state.creditBalance === 1 ? 'Você pode criar 1 torneio.' : `Você pode criar ${Math.max(state.creditBalance, 0)} torneios.`}</p></div></section><button class="btn btn-ghost credit-balance-refresh" type="button" data-action="refresh-credits">Atualizar saldo</button></div>${purchasing}<section class="credit-history-section"><div class="section-title"><h2>Histórico</h2></div><ul class="credit-history">${history}</ul></section>`, 'Créditos', true);
}

function rankingView(ranking, completed, total, title = 'Ranking') {
  return `<div class="ranking-head"><h2>${escapeHtml(title)}</h2><span>${completed}/${total}</span></div>
    ${completed === 0 ? '<p class="ranking-empty">O ranking aparece após o primeiro resultado completo.</p>' : `<ol class="ranking-list">${ranking.map((entry, index) => `<li><span class="ranking-place">${index + 1}º</span><span class="ranking-name">${escapeHtml(entry.name)}<small>${entry.played} ${entry.played === 1 ? 'jogo' : 'jogos'} · V: ${entry.wins} · GA: ${formatGameAverage(entry.gameAverage)}</small></span><strong>${entry.points}</strong></li>`).join('')}</ol>`}`;
}
function tournamentRankingSections(item) {
  const data = Supers.format(item.format);
  if (!data.phases) {
    const result = Supers.standings(item);
    return [{ title: item.name, ranking: result.ranking, completed: result.completed, total: data.games.length }];
  }
  const { a, b, complete } = Supers.groupStandings(item);
  const sections = [
    { title: 'Grupo A', ranking: a.ranking, completed: a.completed, total: 5 },
    { title: 'Grupo B', ranking: b.ranking, completed: b.completed, total: 5 },
  ];
  const seeds = Supers.finalSeeds(item);
  if (complete && seeds) {
    const names = seeds.map((position) => item.athletes[position - 1]);
    const top = Super6.standings(names.slice(0,5), item.scores?.slice(10,15), item.scoring, Supers.five);
    const bottom = Super6.standings(names.slice(5), item.scores?.slice(15,20), item.scoring, Supers.five);
    sections.push(
      { title: 'Final 1º–5º', ranking: top.ranking, completed: top.completed, total: 5 },
      { title: 'Final 6º–10º', ranking: bottom.ranking, completed: bottom.completed, total: 5 },
    );
  }
  return sections;
}
function tournamentRanking(item) {
  const sections = tournamentRankingSections(item);
  const tournamentTitle = sections.length > 1 ? `<div class="ranking-tournament-title"><h2>${escapeHtml(item.name)}</h2></div>` : '';
  return tournamentTitle + sections.map((section) => rankingView(section.ranking, section.completed, section.total, section.title)).join('');
}

function drawRoundedRect(ctx, x, y, width, height, radius, fill, stroke = null) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
  if (fill) { ctx.fillStyle = fill; ctx.fill(); }
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 2; ctx.stroke(); }
}
function drawEllipsizedText(ctx, text, x, y, maxWidth) {
  let value = String(text || '');
  if (ctx.measureText(value).width <= maxWidth) { ctx.fillText(value, x, y); return; }
  while (value.length && ctx.measureText(`${value}…`).width > maxWidth) value = value.slice(0, -1);
  ctx.fillText(`${value}…`, x, y);
}
function rankingImageCanvas(item) {
  const sections = tournamentRankingSections(item);
  const sectionHeights = sections.map((section) => 70 + Math.max(section.ranking.length, 1) * 82);
  const width = 1080;
  const height = Math.max(1350, 370 + sectionHeights.reduce((sum, value) => sum + value, 0) + 265);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#f3f7f4';
  ctx.fillRect(0, 0, width, height);
  drawRoundedRect(ctx, 42, 42, width - 84, height - 84, 28, '#ffffff', '#d7e5dc');

  ctx.fillStyle = '#0b4a34';
  ctx.font = '800 28px Arial, sans-serif';
  ctx.fillText('QUALYSPORTS', 82, 105);
  ctx.fillStyle = '#74877b';
  ctx.font = '700 16px Arial, sans-serif';
  ctx.fillText('SUPERS', 82, 130);
  ctx.fillStyle = '#20362a';
  ctx.font = '700 46px Arial, sans-serif';
  drawEllipsizedText(ctx, item.name, 82, 198, 916);
  ctx.fillStyle = '#16704f';
  ctx.font = '700 20px Arial, sans-serif';
  ctx.fillText(`${item.formatName.toUpperCase()} · ${formatDate(item.date).toUpperCase()}`, 82, 238);
  ctx.strokeStyle = '#e1eae4';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(82, 275);
  ctx.lineTo(998, 275);
  ctx.stroke();

  let y = 320;
  sections.forEach((section) => {
    if (sections.length > 1) {
      ctx.fillStyle = '#20362a';
      ctx.font = '700 25px Arial, sans-serif';
      ctx.fillText(section.title, 82, y);
    }
    ctx.fillStyle = '#eaf6ee';
    drawRoundedRect(ctx, 908, y - 28, 90, 38, 9, '#eaf6ee');
    ctx.fillStyle = '#176b4d';
    ctx.font = '700 18px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${section.completed}/${section.total}`, 953, y - 2);
    ctx.textAlign = 'left';
    y += 42;

    if (!section.completed) {
      ctx.fillStyle = '#7c8d83';
      ctx.font = '400 21px Arial, sans-serif';
      ctx.fillText('O ranking aparece após o primeiro resultado completo.', 82, y + 32);
      y += 82;
      return;
    }
    section.ranking.forEach((entry, index) => {
      ctx.strokeStyle = '#e7eee9';
      ctx.beginPath();
      ctx.moveTo(82, y);
      ctx.lineTo(998, y);
      ctx.stroke();
      ctx.fillStyle = index === 0 ? '#16845e' : '#72877a';
      ctx.font = '700 21px Arial, sans-serif';
      ctx.fillText(`${index + 1}º`, 82, y + 37);
      ctx.fillStyle = '#263a2f';
      ctx.font = '700 22px Arial, sans-serif';
      drawEllipsizedText(ctx, entry.name, 152, y + 29, 665);
      ctx.fillStyle = '#8b9b91';
      ctx.font = '400 16px Arial, sans-serif';
      ctx.fillText(`${entry.played} ${entry.played === 1 ? 'jogo' : 'jogos'} · V: ${entry.wins} · GA: ${formatGameAverage(entry.gameAverage)}`, 152, y + 55);
      ctx.fillStyle = index === 0 ? '#16845e' : '#20362a';
      ctx.font = '700 25px Arial, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(String(entry.points), 998, y + 43);
      ctx.textAlign = 'left';
      y += 82;
    });
    y += 28;
  });

  drawRoundedRect(ctx, 82, y, 916, 200, 18, '#edf7f0', '#d4e8da');
  ctx.fillStyle = '#234032';
  ctx.font = '700 21px Arial, sans-serif';
  ctx.fillText('Critérios de desempate', 112, y + 40);
  ctx.fillStyle = '#667c6f';
  ctx.font = '400 17px Arial, sans-serif';
  ctx.fillText('1. Vitórias', 112, y + 76);
  ctx.fillText('2. Game average: soma de games conquistados ÷ games jogados em cada partida', 112, y + 108);
  ctx.fillText('3. Confronto direto entre os empatados', 112, y + 140);
  ctx.fillText('4. Posição de cadastro, se o empate continuar', 112, y + 172);
  return canvas;
}
async function exportRankingImage(button) {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  if (!item) return;
  const original = button.innerHTML;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'Gerando imagem…';
  try {
    const canvas = rankingImageCanvas(item);
    const blob = await new Promise((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Não foi possível criar a imagem.')), 'image/png'));
    const filename = `ranking-${item.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'super'}.png`;
    const file = new File([blob], filename, { type: 'image/png' });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ title: `Ranking · ${item.name}`, text: `Ranking do ${item.name}`, files: [file] });
        showToast('Imagem pronta para compartilhar.');
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast('Imagem do ranking salva.');
  } catch (error) {
    showToast(error.message || 'Não foi possível gerar a imagem do ranking.', true);
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.innerHTML = original;
  }
}

function configView(item) {
  const mode = item.scoring?.mode || 'sum';
  const target = item.scoring?.target || (mode === 'set' ? 7 : 6);
  const sample = mode === 'sum' ? Math.min(2, target) : Math.min(2, target - 1);
  const priorScores = item.scores?.some((score) => score?.left != null || score?.right != null);
  return `<div class="super6-layout config-layout"><form class="form-card" id="scoring-form"><div class="form-section-head"><span class="step-number">01</span><div><h2>Configuração do torneio</h2></div></div>
    <div class="scoring-options"><label class="scoring-choice ${mode === 'sum' ? 'selected' : ''}"><input type="radio" name="scoring-mode" value="sum" ${mode === 'sum' ? 'checked' : ''} /><span class="scoring-radio"></span><span><strong>Soma de games</strong><small>Os dois placares somam um total fixo.</small></span></label><label class="scoring-choice ${mode === 'set' ? 'selected' : ''}"><input type="radio" name="scoring-mode" value="set" ${mode === 'set' ? 'checked' : ''} /><span class="scoring-radio"></span><span><strong>Set normal</strong><small>Uma dupla chega à quantidade de games definida.</small></span></label></div>
    <div class="form-field scoring-target"><label for="scoring-target" id="scoring-target-label">${mode === 'sum' ? 'Soma de games por jogo' : 'Games para vencer o set'}</label><input id="scoring-target" name="scoring-target" type="number" min="1" step="1" inputmode="numeric" value="${target}" required /><small id="scoring-example">${mode === 'sum' ? `Exemplo: soma ${target} → ${sample} × ${target - sample}.` : `Exemplo: set até ${target} → ${sample} × ${target}.`}</small></div>
    <div class="divider config-divider"></div>
    <div class="form-field registration-fee-field"><label for="registration-fee">Valor da inscrição por atleta</label><div class="currency-input"><span>R$</span><input id="registration-fee" name="registration-fee" type="text" inputmode="decimal" value="${formatCurrencyInput(item.registrationFeeCents)}" placeholder="0,00" required /></div></div>
    ${priorScores ? '<p class="config-warning">Se alterar esta regra, os placares já lançados serão mantidos. Resultados incompatíveis deixarão de contar no ranking até serem corrigidos.</p>' : ''}
    <div class="form-actions"><button class="btn btn-primary" type="submit">Salvar configuração ${icon('arrow')}</button></div></form>
    <section class="share-card"><div><span class="share-kicker">Link público</span><h3>Acompanhamento dos atletas</h3></div><div class="share-card-actions"><span class="share-status ${item.shareEnabled ? 'active' : ''}">${item.shareEnabled ? 'Link ativo' : 'Desativado'}</span><button class="btn btn-ghost" type="button" data-action="share-open">Compartilhar com atletas</button></div></section></div>`;
}

function paymentButton(item, athlete, compact = false) {
  const payment = Supers.paymentFor(item, athlete.index);
  const status = payment.paid ? 'paid' : 'pending';
  const label = payment.paid ? 'Recebido' : 'Pendente';
  if (compact) return `<span class="payment-person compact ${status}" aria-label="${escapeHtml(athlete.name)}: ${label}, ${escapeHtml(formatCurrency(payment.amountCents))}"><span>${escapeHtml(athlete.name)}</span></span>`;
  return `<div class="payment-person-row ${status}"><button class="payment-person-main" type="button" data-action="payment-open" data-payment-index="${athlete.index}" aria-label="Editar pagamento de ${escapeHtml(athlete.name)}"><span>${escapeHtml(athlete.name)}</span><small>${escapeHtml(formatCurrency(payment.amountCents))} · ${label}</small></button><button class="payment-flag ${status}" type="button" data-action="payment-toggle" data-payment-index="${athlete.index}" aria-pressed="${payment.paid}" aria-label="${payment.paid ? 'Marcar como pendente' : 'Marcar como recebido'}: ${escapeHtml(athlete.name)}">${icon('flag')}</button></div>`;
}

function athletePaymentsCard(item) {
  if (!Supers.athletesReady(item)) return '';
  const athletes = Supers.individualAthletes(item);
  const summary = Supers.financialSummary(item);
  return `<section class="athlete-payments-card"><div class="athlete-payments-head"><h3>Pagamentos</h3><span>${summary.paidCount}/${summary.totalCount}</span></div><div class="athlete-payment-list">${athletes.map((athlete) => paymentButton(item, athlete)).join('')}</div></section>`;
}

function athletesView(item) {
  const data = Supers.format(item.format);
  const fields = Array.from({ length: data.count }, (_, index) => {
    if (data.fixed) {
      const names = (item.athletes?.[index] || '').split(' / ');
      return `<fieldset class="duo-field"><legend>Dupla ${index + 1}</legend><div class="duo-inputs"><div class="form-field"><label for="athlete-${index + 1}-a">Jogador 1</label><input id="athlete-${index + 1}-a" name="athlete-${index + 1}-a" value="${escapeHtml(names[0] || '')}" placeholder="Nome do jogador" maxlength="70" required /></div><span>/</span><div class="form-field"><label for="athlete-${index + 1}-b">Jogador 2</label><input id="athlete-${index + 1}-b" name="athlete-${index + 1}-b" value="${escapeHtml(names[1] || '')}" placeholder="Nome do jogador" maxlength="70" required /></div></div></fieldset>`;
    }
    const group = data.phases ? ` · Grupo ${index < 5 ? 'A' : 'B'}` : '';
    return `<div class="form-field"><label for="athlete-${index + 1}">Atleta ${index + 1}${group}</label><input id="athlete-${index + 1}" name="athlete-${index + 1}" type="text" value="${escapeHtml(item.athletes?.[index] || '')}" placeholder="Nome do atleta" maxlength="70" autocomplete="off" required /></div>`;
  }).join('');
  const ready = Supers.athletesReady(item);
  return `<div class="super6-layout"><form class="form-card" id="athletes-form"><div class="form-section-head athlete-section-head"><h2>ATLETAS</h2></div><div class="athlete-grid ${data.fixed ? 'fixed-duos' : ''}">${fields}</div><div class="form-actions"><button class="btn btn-primary" type="submit">Salvar atletas</button></div></form><div class="athlete-side">${athletePaymentsCard(item)}</div>${ready ? `<div class="athletes-financial-summary">${financialSummaryView(item)}</div>` : ''}</div>`;
}

function matchLiveMeta(matchState) {
  if (!matchState) return '<div class="match-live-meta is-empty" aria-hidden="true"><span class="match-court"><small>Quadra</small><strong>–</strong></span><span class="match-timer"><small>Tempo</small><strong>00:00</strong></span></div>';
  const court = matchState.court ? `<span class="match-court"><small>Quadra</small><strong>${escapeHtml(matchState.court)}</strong></span>` : '';
  const endedAt = matchState.endedAt || '';
  return `<div class="match-live-meta">${court}<span class="match-timer"><small>Tempo</small><strong data-match-timer data-started-at="${escapeHtml(matchState.startedAt)}"${endedAt ? ` data-ended-at="${escapeHtml(endedAt)}"` : ''}>${formatElapsed(matchState.startedAt, endedAt ? Date.parse(endedAt) : Date.now())}</strong></span></div>`;
}

function gameCards(item, schedule, labels, offset, perRound, labelIndexes = labels.map((_, index) => index), byes = null) {
  const data = Supers.format(item.format);
  const rounds = [];
  for (let start = 0; start < schedule.length; start += perRound) {
    const matches = schedule.slice(start, start + perRound);
    const roundNumber = Math.floor(start / perRound) + 1;
    const statuses = matches.map((_, index) => Super6.scoreStatus(item.scores?.[offset + start + index], item.scoring));
    const completed = statuses.filter((status) => status === 'complete').length;
    const roundStatus = statuses.includes('invalid') ? 'invalid' : completed === matches.length ? 'complete' : 'pending';
    const roundLabel = `Rodada ${String(roundNumber).padStart(2, '0')}`;
    const roundKey = `${item.id}:${offset + start}`;
    const collapsed = roundStatus === 'complete' && state.collapsedRounds.has(roundKey);
    const roundText = roundStatus === 'invalid' ? 'Placar incompatível'
      : roundStatus === 'pending' && completed ? `${completed} de ${matches.length} jogos lançados` : '';
    const rows = matches.map(([left, right], index) => {
      const gameIndex = start + index;
      const absoluteIndex = offset + gameIndex;
      const score = item.scores?.[offset + gameIndex] || {};
      const status = statuses[index];
      const availability = Supers.matchAvailability(item, absoluteIndex);
      const active = availability.active;
      const savedMatchState = Supers.matchState(item, absoluteIndex);
      const displayedMatchState = active || (status === 'complete' ? savedMatchState : null);
      const blocked = !active && status !== 'complete' && availability.blocked;
      const statusText = status === 'invalid' ? 'Placar incompatível' : active ? 'Em andamento' : blocked ? 'Atleta em jogo' : '';
      const names = (pair) => pair.map((position) => {
        const label = String(labels[position - 1] || '');
        const originalIndex = labelIndexes[position - 1];
        if (data.fixed) {
          return label.split(' / ').slice(0, 2).map((name, playerIndex) => paymentButton(item, { name, index: originalIndex * 2 + playerIndex }, true)).join('<span class="player-separator">/</span>');
        }
        return paymentButton(item, { name: label, index: originalIndex }, true);
      }).join('<span class="player-separator">/</span>');
      const scoreButton = (side, label) => `<button class="score-trigger${score[side] == null ? ' empty' : ''}" type="button" data-action="score-open" data-score-side="${side}" data-round="${offset + gameIndex}" value="${escapeHtml(score[side] ?? '')}" aria-label="${label} na rodada ${roundNumber}, jogo ${index + 1}. Atual: ${escapeHtml(score[side] ?? 'vazio')}" aria-haspopup="dialog" aria-expanded="false" aria-invalid="${status === 'invalid'}">${escapeHtml(score[side] ?? '–')}</button>`;
      const interactive = status !== 'complete' ? ` data-action="match-open" data-match-index="${absoluteIndex}" tabindex="0" role="button" aria-disabled="${blocked}" aria-label="${active ? 'Jogo em andamento' : blocked ? 'Jogo bloqueado por atleta em quadra' : 'Iniciar jogo'}"` : '';
      return `<article class="match-card ${status}${active ? ' active' : ''}${blocked ? ' blocked' : ''}${status !== 'complete' ? ' clickable' : ''}${matches.length === 1 ? ' single-match' : ''}" data-round="${absoluteIndex}"${interactive}>${matches.length > 1 ? `<div class="match-label"><strong>Jogo ${index + 1}</strong>${statusText ? `<span class="match-state">${statusText}</span>` : ''}</div>` : ''}<div class="match-body"><div class="match-team">${names(left)}</div><div class="match-score">${scoreButton('left', 'Games da primeira dupla')}<span class="score-cross">×</span>${scoreButton('right', 'Games da segunda dupla')}</div><div class="match-team">${names(right)}</div>${matchLiveMeta(displayedMatchState)}</div></article>`;
    }).join('');
    const byeNames = (byes?.[roundNumber - 1] || []).map((position) => escapeHtml(String(labels[position - 1] || '—')));
    const bye = byeNames.length ? `<div class="round-bye"><span>Folga</span><strong>${byeNames.join(' · ')}</strong></div>` : '';
    const toggle = roundStatus === 'complete' ? `<button class="round-toggle" type="button" data-action="round-toggle" data-round-key="${escapeHtml(roundKey)}" data-round-label="${roundLabel}" aria-controls="round-matches-${offset + start}" aria-expanded="${!collapsed}" aria-label="${collapsed ? 'Expandir' : 'Minimizar'} ${roundLabel}">${collapsed ? '+' : '−'}</button>` : '';
    rounds.push(`<section class="round-card ${roundStatus}${collapsed ? ' collapsed' : ''}" aria-label="Rodada ${roundNumber}${roundStatus === 'complete' ? ', concluída' : ''}"><div class="round-header"><div class="round-title">${toggle}<strong>${roundLabel}</strong></div>${roundText ? `<span class="round-state">${roundText}</span>` : ''}</div><div class="round-matches" id="round-matches-${offset + start}"${collapsed ? ' hidden' : ''}>${rows}${bye}</div></section>`);
  }
  return rounds.join('');
}
function gamesView(item) {
  const data = Supers.format(item.format);
  if (!item.drawn) return `<div class="games-view"><div class="draw-actions"><button class="btn btn-primary" type="button" data-action="draw-tournament">Sortear distribuição</button></div></div>`;
  let cards;
  if (data.phases) {
    const group = Supers.groupStandings(item);
    cards = `<h3 class="stage-title">Fase 1 · Grupo A</h3><div class="matches">${gameCards(item, data.games.slice(0,5), item.athletes.slice(0,5), 0, 1, [0,1,2,3,4])}</div><h3 class="stage-title">Fase 1 · Grupo B</h3><div class="matches">${gameCards(item, Supers.five, item.athletes.slice(5,10), 5, 1, [5,6,7,8,9])}</div>`;
    const seeds = Supers.finalSeeds(item);
    if (seeds) {
      const labels = seeds.map((position) => item.athletes[position - 1]);
      const indexes = seeds.map((position) => position - 1);
      cards += `<h3 class="stage-title">Fase 2 · Final 1º–5º</h3><div class="seed-list">${seeds.slice(0,5).map((position) => `<span>${escapeHtml(item.athletes[position - 1])}</span>`).join('')}</div><div class="matches">${gameCards(item, Supers.five, labels.slice(0,5), 10, 1, indexes.slice(0,5))}</div><h3 class="stage-title">Fase 2 · Final 6º–10º</h3><div class="seed-list">${seeds.slice(5).map((position) => `<span>${escapeHtml(item.athletes[position - 1])}</span>`).join('')}</div><div class="matches">${gameCards(item, Supers.five, labels.slice(5), 15, 1, indexes.slice(5))}</div>`;
    } else cards += '<div class="phase-note">A segunda fase será formada automaticamente após os dez jogos dos grupos. Classificam-se para 1º–5º os dois primeiros de cada grupo e o melhor terceiro.</div>';
  } else cards = `<div class="matches">${gameCards(item, data.games, item.athletes, 0, data.perRound, undefined, data.byes)}</div>`;
  const completed = data.phases ? (item.scores || []).filter((score) => Super6.isComplete(score, item.scoring)).length : Supers.standings(item).completed;
  return `<div class="games-view"><div class="games-head progress-only"><span id="games-progress">${completed} de ${data.phases ? 20 : data.games.length} lançados</span></div>${cards}<div class="draw-actions is-bottom"><button class="btn btn-ghost" type="button" data-action="draw-tournament">Sortear novamente</button></div></div>`;
}

function financialSummaryView(item) {
  const summary = Supers.financialSummary(item);
  return `<section class="financial-summary"><div class="financial-summary-head"><div><h3>Resumo financeiro</h3><p>Inscrições recebidas e pendentes</p></div><span>${summary.paidCount}/${summary.totalCount} recebidos</span></div><div class="financial-summary-grid"><div class="received"><small>Recebido</small><strong>${escapeHtml(formatCurrency(summary.receivedCents))}</strong></div><div class="pending"><small>Pendente</small><strong>${escapeHtml(formatCurrency(summary.pendingCents))}</strong></div><div><small>Total previsto</small><strong>${escapeHtml(formatCurrency(summary.expectedCents))}</strong></div></div></section>`;
}

function rankingTabView(item) {
  return `<div class="ranking-tab-content"><section class="ranking-card ranking-page" id="super6-ranking">${tournamentRanking(item)}</section><aside class="ranking-rules"><h3>Critérios de desempate</h3><ol><li>Vitórias</li><li>Game average: soma de games conquistados ÷ games jogados em cada partida</li><li>Confronto direto entre os empatados</li><li>Posição de cadastro, se o empate continuar</li></ol></aside><div class="ranking-export-actions"><button class="btn btn-primary ranking-export-button" type="button" data-action="export-ranking">${icon('download')} Salvar imagem</button></div></div>`;
}

function publicGameCards(item, schedule, labels, offset, perRound, byes = null) {
  const rounds = [];
  for (let start = 0; start < schedule.length; start += perRound) {
    const matches = schedule.slice(start, start + perRound);
    const roundNumber = Math.floor(start / perRound) + 1;
    const statuses = matches.map((_, index) => Super6.scoreStatus(item.scores?.[offset + start + index], item.scoring));
    const completed = statuses.filter((status) => status === 'complete').length;
    const roundStatus = statuses.includes('invalid') ? 'invalid' : completed === matches.length ? 'complete' : 'pending';
    const roundLabel = `Rodada ${String(roundNumber).padStart(2, '0')}`;
    const roundKey = `public:${item.id}:${offset + start}`;
    const collapsed = roundStatus === 'complete' && state.collapsedRounds.has(roundKey);
    const roundText = roundStatus === 'invalid' ? 'Placar incompatível' : roundStatus === 'pending' && completed ? `${completed} de ${matches.length} jogos lançados` : '';
    const rows = matches.map(([left, right], index) => {
      const absoluteIndex = offset + start + index;
      const score = item.scores?.[offset + start + index] || {};
      const status = statuses[index];
      const active = Supers.activeMatchState(item, absoluteIndex);
      const savedMatchState = Supers.matchState(item, absoluteIndex);
      const displayedMatchState = active || (status === 'complete' ? savedMatchState : null);
      const team = (pair) => pair.map((position) => escapeHtml(String(labels[position - 1] || '—'))).join('<span class="player-separator">/</span>');
      const scoreValue = (side) => `<span class="public-score ${score[side] == null ? 'empty' : ''}">${escapeHtml(score[side] ?? '–')}</span>`;
      const statusText = status === 'invalid' ? 'Placar incompatível' : active ? 'Em andamento' : '';
      return `<article class="match-card ${status}${active ? ' active' : ''}${matches.length === 1 ? ' single-match' : ''}">${matches.length > 1 ? `<div class="match-label"><strong>Jogo ${index + 1}</strong>${statusText ? `<span class="match-state">${statusText}</span>` : ''}</div>` : ''}<div class="match-body"><div class="match-team public-team">${team(left)}</div><div class="match-score">${scoreValue('left')}<span class="score-cross">×</span>${scoreValue('right')}</div><div class="match-team public-team">${team(right)}</div>${matchLiveMeta(displayedMatchState)}</div></article>`;
    }).join('');
    const byeNames = (byes?.[roundNumber - 1] || []).map((position) => escapeHtml(String(labels[position - 1] || '—')));
    const bye = byeNames.length ? `<div class="round-bye"><span>Folga</span><strong>${byeNames.join(' · ')}</strong></div>` : '';
    const toggle = roundStatus === 'complete' ? `<button class="round-toggle" type="button" data-action="round-toggle" data-round-key="${escapeHtml(roundKey)}" data-round-label="${roundLabel}" aria-controls="public-round-matches-${offset + start}" aria-expanded="${!collapsed}" aria-label="${collapsed ? 'Expandir' : 'Minimizar'} ${roundLabel}">${collapsed ? '+' : '−'}</button>` : '';
    rounds.push(`<section class="round-card ${roundStatus}${collapsed ? ' collapsed' : ''}" aria-label="Rodada ${roundNumber}${roundStatus === 'complete' ? ', concluída' : ''}"><div class="round-header"><div class="round-title">${toggle}<strong>${roundLabel}</strong></div>${roundText ? `<span class="round-state">${roundText}</span>` : ''}</div><div class="round-matches" id="public-round-matches-${offset + start}"${collapsed ? ' hidden' : ''}>${rows}${bye}</div></section>`);
  }
  return rounds.join('');
}
function publicGamesView(item) {
  const data = Supers.format(item.format);
  if (!data || !Super6.validConfig(item.scoring) || !Supers.athletesReady(item) || !item.drawn) return '<div class="public-empty"><div class="empty-icon">' + icon('trophy') + '</div><h2>Aguardando sorteio</h2><p>Os jogos aparecerão aqui assim que o organizador fizer o sorteio.</p></div>';
  let cards;
  if (data.phases) {
    cards = `<h3 class="stage-title">Fase 1 · Grupo A</h3><div class="matches">${publicGameCards(item, data.games.slice(0, 5), item.athletes.slice(0, 5), 0, 1)}</div><h3 class="stage-title">Fase 1 · Grupo B</h3><div class="matches">${publicGameCards(item, Supers.five, item.athletes.slice(5, 10), 5, 1)}</div>`;
    const seeds = Supers.finalSeeds(item);
    if (seeds) {
      const labels = seeds.map((position) => item.athletes[position - 1]);
      cards += `<h3 class="stage-title">Fase 2 · Final 1º–5º</h3><div class="matches">${publicGameCards(item, Supers.five, labels.slice(0, 5), 10, 1)}</div><h3 class="stage-title">Fase 2 · Final 6º–10º</h3><div class="matches">${publicGameCards(item, Supers.five, labels.slice(5), 15, 1)}</div>`;
    } else cards += '<div class="phase-note">A segunda fase aparece após a conclusão dos jogos dos grupos.</div>';
  } else cards = `<div class="matches">${publicGameCards(item, data.games, item.athletes, 0, data.perRound, data.byes)}</div>`;
  const completed = data.phases ? item.scores.filter((score) => Super6.isComplete(score, item.scoring)).length : Supers.standings(item).completed;
  return `<div class="games-view public-games"><div class="games-head progress-only"><span>${completed} de ${data.phases ? 20 : data.games.length} lançados</span></div>${cards}</div>`;
}
function publicRankingView(item) {
  if (!Supers.format(item.format) || !Super6.validConfig(item.scoring) || !Supers.athletesReady(item) || !item.drawn) return '<div class="public-empty"><div class="empty-icon">' + icon('trophy') + '</div><h2>Ranking ainda não disponível</h2><p>O ranking aparecerá depois do sorteio e dos primeiros resultados.</p></div>';
  return `<div class="ranking-tab-content"><section class="ranking-card ranking-page">${tournamentRanking(item)}</section><aside class="ranking-rules"><h3>Critérios de desempate</h3><ol><li>Vitórias</li><li>Game average</li><li>Confronto direto entre os empatados</li><li>Posição de cadastro, se o empate continuar</li></ol></aside></div>`;
}
function publicView() {
  if (state.publicError || !state.publicItem) return `<div class="public-shell public-unavailable"><header class="public-topbar">${brand()}<span>Somente leitura</span></header><main class="public-content"><div class="public-empty"><div class="empty-icon">${icon('info')}</div><h1>Link indisponível</h1><p>${escapeHtml(state.publicError || 'Este link não está disponível.')}</p></div></main></div>`;
  const item = state.publicItem;
  const updated = state.publicUpdatedAt ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(state.publicUpdatedAt) : '';
  return `<div class="public-shell"><header class="public-topbar">${brand()}<span>Somente leitura</span></header><main class="public-content"><div class="public-title"><span class="eyebrow">${escapeHtml(item.formatName)} · ${formatDate(item.date)}</span><h1>${escapeHtml(item.name)}</h1></div><div class="public-tabs" role="tablist" aria-label="Acompanhamento do torneio"><button type="button" role="tab" aria-selected="${state.publicTab === 'games'}" class="${state.publicTab === 'games' ? 'active' : ''}" data-action="public-tab" data-tab="games">Jogos</button><button type="button" role="tab" aria-selected="${state.publicTab === 'ranking'}" class="${state.publicTab === 'ranking' ? 'active' : ''}" data-action="public-tab" data-tab="ranking">Ranking</button></div><section class="public-panel">${state.publicTab === 'ranking' ? publicRankingView(item) : publicGamesView(item)}</section><footer class="public-refresh"><span></span>Atualização automática${updated ? ` · ${updated}` : ''}</footer></main></div>`;
}

function detailView() {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  if (!item) { state.page = 'home'; return homeView(); }
  const data = Supers.format(item.format);
  const configured = !!data && Super6.validConfig(item.scoring);
  const ready = configured && Supers.athletesReady(item);
  const drawn = ready && item.drawn;
  if (!configured) state.detailTab = 'config';
  else if (!ready && ['games', 'ranking'].includes(state.detailTab)) state.detailTab = 'athletes';
  else if (!drawn && state.detailTab === 'ranking') state.detailTab = 'games';
  const tab = (key, number, label, enabled) => `<button type="button" role="tab" aria-selected="${state.detailTab === key}" class="${state.detailTab === key ? 'active' : ''}" data-action="detail-tab" data-tab="${key}" ${enabled ? '' : 'disabled'}><span>${number}</span> ${label}</button>`;
  const tabs = data ? `<div class="detail-tabs" role="tablist" aria-label="Etapas do ${escapeHtml(item.formatName)}">${tab('config', 1, 'Configuração', true)}${tab('athletes', 2, data.fixed ? 'Duplas' : 'Atletas', configured)}${tab('games', 3, 'Jogos', ready)}${tab('ranking', 4, 'Ranking', drawn)}</div>` : '';
  const content = !data ? '<div class="detail-card">Este formato não está disponível para novos torneios.</div>'
    : state.detailTab === 'config' ? configView(item)
      : state.detailTab === 'games' && ready ? gamesView(item)
        : state.detailTab === 'ranking' && drawn ? rankingTabView(item)
          : athletesView(item);
  const description = !data || !configured ? '' : !ready ? `Agora cadastre ${data.fixed ? 'as oito duplas' : `os ${data.count} atletas`}.` : '';
  const footer = state.detailTab === 'config' ? `<footer class="tournament-footer-actions"><button class="btn btn-danger" type="button" data-action="delete-tournament" data-id="${escapeHtml(item.id)}">${icon('trash')} Excluir torneio</button></footer>` : '';
  return shell(`<button class="back-link" type="button" data-action="home">${icon('back')} Voltar para meus Supers</button><div class="page-head"><div><span class="eyebrow">${escapeHtml(item.formatName)} · ${formatDate(item.date)}</span><h1>${escapeHtml(item.name)}</h1>${description ? `<p>${description}</p>` : ''}</div></div>${tabs}${content}${footer}`, 'Detalhes do Super', true);
}

function updateMatchTimers() {
  const now = Date.now();
  document.querySelectorAll('[data-match-timer]').forEach((timer) => {
    const endedAt = Date.parse(timer.dataset.endedAt || '');
    timer.textContent = formatElapsed(timer.dataset.startedAt, Number.isFinite(endedAt) ? endedAt : now);
  });
}
function render(preserveScroll = false) {
  const scroll = preserveScroll ? window.scrollY : 0;
  if (!state.publicToken) closeScorePicker();
  app.innerHTML = state.loading ? '<div class="app-loading" role="status"><span></span><p>Carregando Supers…</p></div>' : state.configError ? '<div class="app-config-error"><h1>Configuração pendente</h1><p>A conexão com o Supabase ainda não foi configurada neste ambiente.</p></div>' : state.publicToken ? publicView() : !state.user ? authView() : state.page === 'users' && isAdminSession() ? usersView() : state.page === 'credits' ? creditsView() : state.page === 'create' ? createView() : state.page === 'detail' ? detailView() : homeView();
  window.scrollTo(0, scroll);
  updateMatchTimers();
}
function showToast(message, error = false) {
  document.querySelector('.toast')?.remove();
  clearTimeout(state.toastTimer);
  const el = document.createElement('div');
  el.className = `toast${error ? ' error' : ''}`;
  el.setAttribute('role', 'status');
  el.textContent = message;
  document.body.append(el);
  state.toastTimer = setTimeout(() => el.remove(), 4500);
}

function openPaymentDialog(index) {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  const athlete = Supers.individualAthletes(item).find((entry) => entry.index === index);
  if (!item || !athlete) return;
  document.querySelector('.payment-dialog')?.remove();
  const payment = Supers.paymentFor(item, index);
  const dialog = document.createElement('dialog');
  dialog.className = 'payment-dialog';
  dialog.innerHTML = `<form method="dialog" id="payment-form"><div class="payment-dialog-head"><h2>${escapeHtml(athlete.name)}</h2><button type="button" class="payment-dialog-close" aria-label="Fechar">×</button></div><div class="form-field"><label for="athlete-fee">Valor da inscrição</label><div class="currency-input"><span>R$</span><input id="athlete-fee" name="athlete-fee" type="text" inputmode="decimal" value="${formatCurrencyInput(payment.amountCents)}" required /></div></div><label class="payment-received-toggle"><input name="payment-received" type="checkbox" ${payment.paid ? 'checked' : ''} /><span></span><strong>Pagamento recebido</strong></label><div class="payment-dialog-actions"><button class="btn btn-ghost" type="button" data-payment-cancel>Cancelar</button><button class="btn btn-primary" type="submit">Salvar</button></div></form>`;
  document.body.append(dialog);
  const close = () => dialog.close();
  dialog.querySelector('.payment-dialog-close').addEventListener('click', close);
  dialog.querySelector('[data-payment-cancel]').addEventListener('click', close);
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  dialog.addEventListener('close', () => dialog.remove());
  dialog.querySelector('form').addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const amountCents = parseCurrencyToCents(new FormData(form).get('athlete-fee'));
    if (amountCents == null) { showToast('Informe um valor de inscrição válido.', true); return; }
    const submit = form.querySelector('button[type="submit"]');
    submit.disabled = true;
    const payments = Array.isArray(item.payments) ? [...item.payments] : [];
    while (payments.length <= index) payments.push(null);
    payments[index] = { amountCents, paid: form.elements['payment-received'].checked };
    const scroll = window.scrollY;
    try {
      await saveTournament({ ...item, payments });
      dialog.close();
      render();
      window.scrollTo(0, scroll);
      showToast(form.elements['payment-received'].checked ? 'Pagamento marcado como recebido.' : 'Pagamento atualizado como pendente.');
    } catch (error) {
      submit.disabled = false;
      showToast(error.message || 'Não foi possível atualizar o pagamento.', true);
    }
  });
  dialog.showModal();
  dialog.querySelector('#athlete-fee').select();
}

function openMatchDialog(index) {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  const data = Supers.format(item?.format);
  const total = data?.phases ? 20 : data?.games.length || 0;
  if (!item || !Number.isInteger(index) || index < 0 || index >= total) return;
  if (Super6.isComplete(item.scores?.[index], item.scoring)) {
    showToast('Este jogo já foi encerrado.');
    return;
  }
  const availability = Supers.matchAvailability(item, index);
  if (!availability.players.length) {
    showToast('Este confronto ainda não está disponível.', true);
    return;
  }
  if (!availability.active && availability.blocked) {
    const shared = Supers.matchAthleteIndexes(item, availability.blockingIndex)
      .filter((player) => availability.players.includes(player))
      .map((player) => item.athletes[player])
      .filter(Boolean);
    showToast(`${shared.join(' / ') || 'Um dos atletas'} já está em outro jogo. Lance o resultado antes de iniciar este confronto.`, true);
    return;
  }
  document.querySelector('.payment-dialog')?.remove();
  const dialog = document.createElement('dialog');
  dialog.className = 'payment-dialog match-start-dialog';
  const close = () => dialog.close();
  if (availability.active) {
    dialog.innerHTML = `<form id="match-court-form"><div class="payment-dialog-head"><div><span class="eyebrow">Jogo em andamento</span><h2>Alterar quadra</h2></div><button type="button" class="payment-dialog-close" aria-label="Fechar">×</button></div><div class="match-dialog-running"><span></span><div><small>Tempo de jogo</small><strong data-match-timer data-started-at="${escapeHtml(availability.active.startedAt)}">${formatElapsed(availability.active.startedAt)}</strong></div></div><div class="form-field match-court-edit"><label for="match-court">Número da quadra <small>(opcional)</small></label><input id="match-court" name="court" type="text" maxlength="20" autocomplete="off" value="${escapeHtml(availability.active.court || '')}" placeholder="Ex.: 2" /></div><p class="match-dialog-note">Alterar a quadra não reinicia o cronômetro.</p><div class="payment-dialog-actions"><button class="btn btn-ghost" type="button" data-match-cancel>Cancelar</button><button class="btn btn-primary" type="submit">Salvar quadra</button></div></form>`;
  } else {
    dialog.innerHTML = `<form id="match-start-form"><div class="payment-dialog-head"><div><span class="eyebrow">Chamar confronto</span><h2>Iniciar jogo</h2></div><button type="button" class="payment-dialog-close" aria-label="Fechar">×</button></div><div class="form-field"><label for="match-court">Número da quadra <small>(opcional)</small></label><input id="match-court" name="court" type="text" maxlength="20" autocomplete="off" placeholder="Ex.: 2" /></div><p class="match-dialog-note">Ao iniciar, o cronômetro começa e outros jogos destes atletas ficam bloqueados.</p><div class="payment-dialog-actions"><button class="btn btn-ghost" type="button" data-match-cancel>Cancelar</button><button class="btn btn-primary" type="submit">Iniciar jogo</button></div></form>`;
  }
  document.body.append(dialog);
  dialog.querySelector('.payment-dialog-close').addEventListener('click', close);
  dialog.querySelector('[data-match-cancel]').addEventListener('click', close);
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  dialog.addEventListener('close', () => dialog.remove());
  dialog.querySelector('#match-court-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const latest = tournaments().find((entry) => entry.id === state.selectedId);
    const active = latest ? Supers.activeMatchState(latest, index) : null;
    if (!latest || !active) {
      close();
      showToast('Este jogo já não está em andamento.', true);
      return;
    }
    const form = event.currentTarget;
    const submit = form.querySelector('button[type="submit"]');
    const court = String(new FormData(form).get('court') || '').trim();
    const matchStates = Array.isArray(latest.matchStates) ? [...latest.matchStates] : [];
    matchStates[index] = { ...matchStates[index], startedAt: active.startedAt, court: court || null };
    submit.disabled = true;
    const scroll = window.scrollY;
    try {
      await saveTournament({ ...latest, matchStates });
      close();
      render();
      window.scrollTo(0, scroll);
      showToast(court ? `Quadra alterada para ${court}.` : 'Número da quadra removido.');
    } catch (error) {
      submit.disabled = false;
      showToast(error.message || 'Não foi possível alterar a quadra.', true);
    }
  });
  dialog.querySelector('#match-start-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const latest = tournaments().find((entry) => entry.id === state.selectedId);
    if (!latest) { close(); return; }
    const latestAvailability = Supers.matchAvailability(latest, index);
    if (latestAvailability.active || latestAvailability.blocked || Super6.isComplete(latest.scores?.[index], latest.scoring)) {
      close();
      showToast('O estado deste jogo mudou. Atualize a tela e tente novamente.', true);
      return;
    }
    const form = event.currentTarget;
    const submit = form.querySelector('button[type="submit"]');
    const court = String(new FormData(form).get('court') || '').trim();
    const matchStates = Array.isArray(latest.matchStates) ? [...latest.matchStates] : [];
    while (matchStates.length <= index) matchStates.push(null);
    matchStates[index] = { startedAt: new Date().toISOString(), court: court || null };
    submit.disabled = true;
    const scroll = window.scrollY;
    try {
      await saveTournament({ ...latest, matchStates });
      close();
      render();
      window.scrollTo(0, scroll);
      showToast(court ? `Jogo iniciado na quadra ${court}.` : 'Jogo iniciado.');
    } catch (error) {
      submit.disabled = false;
      showToast(error.message || 'Não foi possível iniciar o jogo.', true);
    }
  });
  dialog.showModal();
  updateMatchTimers();
  const courtInput = dialog.querySelector('#match-court');
  courtInput?.focus();
  if (availability.active && courtInput?.value) courtInput.select();
}

function openCreditGrantDialog(userId) {
  const user = state.users.find((entry) => entry.id === userId);
  if (!user || !isAdminSession()) return;
  document.querySelector('.payment-dialog')?.remove();
  const dialog = document.createElement('dialog');
  dialog.className = 'payment-dialog credit-grant-dialog';
  dialog.innerHTML = `<form id="credit-grant-form"><div class="payment-dialog-head"><div><span class="eyebrow">Crédito manual</span><h2>${escapeHtml(user.name)}</h2><p>Saldo atual: <strong>${user.creditBalance}</strong></p></div><button type="button" class="payment-dialog-close" aria-label="Fechar">×</button></div><div class="form-field"><label for="credit-grant-amount">Quantidade de créditos</label><input id="credit-grant-amount" name="amount" type="number" min="1" max="10000" step="1" value="1" required /></div><div class="form-field"><label for="credit-grant-note">Observação (opcional)</label><input id="credit-grant-note" name="note" type="text" maxlength="200" placeholder="Ex.: Cortesia comercial" /></div><div class="payment-dialog-actions"><button class="btn btn-ghost" type="button" data-credit-cancel>Cancelar</button><button class="btn btn-primary" type="submit">Adicionar créditos</button></div></form>`;
  document.body.append(dialog);
  const close = () => dialog.close();
  dialog.querySelector('.payment-dialog-close').addEventListener('click', close);
  dialog.querySelector('[data-credit-cancel]').addEventListener('click', close);
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  dialog.addEventListener('close', () => dialog.remove());
  dialog.querySelector('form').addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    const amount = Number(values.get('amount'));
    const note = String(values.get('note') || '').trim();
    if (!Number.isSafeInteger(amount) || amount < 1 || amount > 10000) { showToast('Informe uma quantidade válida de créditos.', true); return; }
    const submit = form.querySelector('button[type="submit"]');
    submit.disabled = true;
    try {
      const { error } = await supabase.from('admin_credit_grants').insert({ user_id: user.id, amount, note: note || null, created_by: state.admin.id });
      if (error) throw error;
      await loadAdminData();
      dialog.close();
      render(true);
      showToast(`${amount} ${amount === 1 ? 'crédito adicionado' : 'créditos adicionados'} para ${user.name}.`);
    } catch (error) {
      submit.disabled = false;
      showToast(error.message || 'Não foi possível adicionar os créditos.', true);
    }
  });
  dialog.showModal();
  dialog.querySelector('#credit-grant-amount').select();
}

async function startCheckout(button) {
  const credits = Number(button.dataset.credits);
  if (![5, 10].includes(credits) || isImpersonating()) return;
  button.disabled = true;
  try {
    const { data, error } = await supabase.functions.invoke('create-checkout-session', { body: { credits } });
    if (error) {
      let message = error.message;
      try { message = (await error.context?.json())?.error || message; } catch { /* resposta sem JSON */ }
      throw new Error(message);
    }
    if (!data?.url) throw new Error('O pagamento não retornou um endereço válido.');
    window.location.assign(data.url);
  } catch (error) {
    button.disabled = false;
    showToast(error.message || 'Não foi possível iniciar o pagamento.', true);
  }
}

function publicShareUrl(token) {
  return `${window.location.origin}/#/acompanhar/${token}`;
}
async function copyShareLink(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement('textarea');
  input.value = value;
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.append(input);
  input.select();
  const copied = document.execCommand('copy');
  input.remove();
  if (!copied) throw new Error('Não foi possível copiar o link.');
}
function openShareDialog() {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  if (!item) return;
  document.querySelector('.share-dialog')?.remove();
  const active = item.shareEnabled && item.shareToken;
  const url = active ? publicShareUrl(item.shareToken) : '';
  const dialog = document.createElement('dialog');
  dialog.className = 'payment-dialog share-dialog';
  dialog.innerHTML = `<div class="share-dialog-content"><div class="payment-dialog-head"><div><h2>Compartilhar com atletas</h2></div><button type="button" class="payment-dialog-close" aria-label="Fechar">×</button></div>${active ? `<label class="share-url-label" for="share-url">Link dos atletas</label><div class="share-url-row"><input id="share-url" type="text" value="${escapeHtml(url)}" readonly /><button class="btn btn-primary" type="button" data-share-copy>Copiar link</button></div><p class="share-dialog-note">O link mostra somente os jogos e o ranking e atualiza automaticamente.</p><div class="share-dialog-secondary"><button class="btn btn-ghost" type="button" data-share-regenerate>Gerar novo link</button><button class="btn btn-danger" type="button" data-share-disable>Desativar link</button></div>` : `<div class="share-disabled-state"><p>Crie um link para os atletas acompanharem jogos e ranking sem entrar na plataforma.</p><button class="btn btn-primary" type="button" data-share-generate>Gerar link</button></div>`}</div>`;
  document.body.append(dialog);
  const close = () => dialog.close();
  const replaceWithLatest = () => { close(); render(true); openShareDialog(); };
  const saveShare = async (button, enabled) => {
    button.disabled = true;
    try {
      const shareToken = enabled ? crypto.randomUUID() : null;
      await saveTournament({ ...item, shareEnabled: enabled, shareToken });
      replaceWithLatest();
      showToast(enabled ? 'Link público pronto para compartilhar.' : 'Link público desativado.');
    } catch (error) {
      button.disabled = false;
      showToast(error.message || 'Não foi possível atualizar o link.', true);
    }
  };
  dialog.querySelector('.payment-dialog-close').addEventListener('click', close);
  dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
  dialog.addEventListener('close', () => dialog.remove());
  dialog.querySelector('[data-share-copy]')?.addEventListener('click', async (event) => {
    try {
      await copyShareLink(url);
      event.currentTarget.textContent = 'Copiado';
      showToast('Link copiado.');
    } catch (error) { showToast(error.message || 'Não foi possível copiar o link.', true); }
  });
  dialog.querySelector('[data-share-generate]')?.addEventListener('click', (event) => saveShare(event.currentTarget, true));
  dialog.querySelector('[data-share-regenerate]')?.addEventListener('click', (event) => {
    if (window.confirm('Gerar um novo link?\n\nO link atual deixará de funcionar.')) saveShare(event.currentTarget, true);
  });
  dialog.querySelector('[data-share-disable]')?.addEventListener('click', (event) => {
    if (window.confirm('Desativar o link público?\n\nOs atletas não conseguirão mais acessar este link.')) saveShare(event.currentTarget, false);
  });
  dialog.showModal();
  dialog.querySelector('#share-url')?.select();
}

async function handleAuth(form) {
  const values = new FormData(form);
  const email = String(values.get('email') || '').trim().toLowerCase();
  const password = String(values.get('password') || '');
  const button = form.querySelector('button[type="submit"]');
  button.disabled = true;
  try {
    if (form.dataset.mode === 'register') {
      const name = String(values.get('name') || '').trim();
      if (name.length < 2) throw new Error('Informe seu nome para continuar.');
      if (password !== values.get('confirm')) throw new Error('As senhas não conferem.');
      const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { name } } });
      if (error) throw error;
      if (!data.session) {
        state.authMode = 'login';
        render();
        showToast('Cadastro recebido. Confira seu e-mail para confirmar a conta e depois faça login.');
        return;
      }
      await establishSession(data.user);
      render();
      showToast('Conta criada. Bem-vindo!');
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      await establishSession(data.user);
      render();
      showToast(state.admin ? 'Acesso administrativo liberado.' : 'Bem-vindo de volta!');
    }
  } catch (error) {
    const message = /Invalid login credentials/i.test(error.message || '') ? 'E-mail ou senha incorretos.' : error.message || 'Não foi possível continuar.';
    showToast(message, true);
  } finally { button.disabled = false; }
}

async function handlePasswordRecoveryRequest(form) {
  const email = String(new FormData(form).get('recovery-email') || '').trim().toLowerCase();
  const button = form.querySelector('button[type="submit"]');
  button.disabled = true;
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: passwordRecoveryRedirectUrl() });
    if (error) throw error;
    showToast('Se existir uma conta para este e-mail, enviaremos um link de recuperação.');
  } catch (error) {
    showToast(error.message || 'Não foi possível enviar o link de recuperação.', true);
  } finally { button.disabled = false; }
}

async function handlePasswordReset(form) {
  const values = new FormData(form);
  const password = String(values.get('new-password') || '');
  const confirmation = String(values.get('new-password-confirm') || '');
  const button = form.querySelector('button[type="submit"]');
  button.disabled = true;
  try {
    if (password.length < 8) throw new Error('A nova senha precisa ter no mínimo 8 caracteres.');
    if (password !== confirmation) throw new Error('As senhas não conferem.');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
    await supabase.auth.signOut();
    clearPasswordRecoveryUrl();
    state.passwordRecovery = false;
    state.authMode = 'login';
    state.user = null;
    state.authUser = null;
    render();
    showToast('Senha alterada. Entre com a sua nova senha.');
  } catch (error) {
    showToast(error.message || 'Não foi possível alterar a senha.', true);
  } finally { button.disabled = false; }
}

async function handleCreate(form) {
  const values = new FormData(form);
  const format = FORMATS.find((entry) => entry.id === values.get('format'));
  const name = String(values.get('tournament-name') || '').trim();
  const date = String(values.get('tournament-date') || '');
  if (!name || !date || !format) { showToast('Preencha nome e data e selecione o tipo de Super.', true); return; }
  if (state.creditBalance <= 0) { state.page = isImpersonating() ? 'home' : 'credits'; render(); showToast('Você precisa de 1 crédito para criar um novo Super.', true); return; }
  const button = form.querySelector('button[type="submit"]');
  button.disabled = true;
  try {
    const valuesToSave = { user_id: state.user.id, name, date, format: format.id, format_name: format.name, athletes: [], scores: [] };
    const { data, error } = await supabase.from('tournaments').insert(valuesToSave).select().single();
    if (error) throw error;
    const item = fromTournamentRow(data);
    state.tournamentItems = [item, ...state.tournamentItems];
    if (isAdminSession()) state.adminTournaments = [item, ...state.adminTournaments];
    await loadCreditData(state.user.id);
    if (isImpersonating()) {
      const adminUser = state.users.find((entry) => entry.id === state.user.id);
      if (adminUser) adminUser.creditBalance = state.creditBalance;
    }
    state.selectedId = item.id;
    state.page = 'detail';
    state.detailTab = 'config';
    render();
    showToast('Seu Super foi criado com sucesso!');
  } catch (error) { button.disabled = false; showToast(error.message || 'Não foi possível criar o Super.', true); }
}

async function handleScoring(form) {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  if (!item || !Supers.format(item.format)) return;
  const values = new FormData(form);
  const mode = String(values.get('scoring-mode') || '');
  const target = Number(values.get('scoring-target'));
  const registrationFeeCents = parseCurrencyToCents(values.get('registration-fee'));
  const scoring = { mode, target };
  if (!Super6.validConfig(scoring)) { showToast('Informe um número inteiro de games maior que zero.', true); return; }
  if (registrationFeeCents == null) { showToast('Informe um valor de inscrição válido.', true); return; }
  const updated = { ...item, scoring, registrationFeeCents };
  if (Supers.format(item.format).phases && item.finalSeed && Supers.finalSeeds(updated) && item.finalSeed !== Supers.finalSeeds(updated).join(',')) {
    updated.scores = (item.scores || []).slice(0, 10);
    updated.matchStates = (item.matchStates || []).slice(0, 10);
    updated.finalSeed = null;
  }
  try {
    await saveTournament(updated);
    state.detailTab = Supers.athletesReady(item) ? 'games' : 'athletes';
    render();
    showToast('Configuração do torneio salva.');
  } catch (error) { showToast(error.message || 'Não foi possível salvar a configuração.', true); }
}

async function handleAthletes(form) {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  const data = Supers.format(item?.format);
  if (!data || !Super6.validConfig(item.scoring)) return;
  const values = new FormData(form);
  const athletes = Array.from({ length: data.count }, (_, index) => data.fixed
    ? [values.get(`athlete-${index + 1}-a`), values.get(`athlete-${index + 1}-b`)].map((name) => String(name || '').trim()).join(' / ')
    : String(values.get(`athlete-${index + 1}`) || '').trim());
  if (athletes.some((name) => !name || (data.fixed && name.split(' / ').some((part) => !part)))) { showToast('Preencha todos os nomes dos participantes.', true); return; }
  const individualNames = data.fixed ? athletes.flatMap((name) => name.split(' / ')) : athletes;
  if (new Set(individualNames.map((name) => name.toLocaleLowerCase('pt-BR'))).size !== individualNames.length) { showToast('Cada participante precisa ter um nome diferente.', true); return; }
  try {
    await saveTournament({ ...item, athletes });
    state.detailTab = 'games';
    render();
    showToast('Atletas salvos.');
  } catch (error) { showToast(error.message || 'Não foi possível salvar os participantes.', true); }
}

async function drawTournament(button) {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  if (!item || !Super6.validConfig(item.scoring) || !Supers.athletesReady(item)) return;
  const hasScores = (item.scores || []).some((score) => score?.left != null || score?.right != null);
  const hasActiveMatches = (item.matchStates || []).some((_, index) => Supers.activeMatchState(item, index));
  const message = item.drawn
    ? `Deseja sortear a distribuição novamente?${hasScores ? '\n\nOs placares lançados serão apagados.' : ''}${hasActiveMatches ? '\n\nOs jogos em andamento serão encerrados.' : ''}`
    : 'Deseja confirmar o sorteio da distribuição dos jogos?';
  if (!window.confirm(message)) return;
  const shuffled = Supers.shuffledParticipants(item, item.athletes);
  button.disabled = true;
  try {
    await saveTournament({ ...item, athletes: shuffled.athletes, payments: shuffled.payments, scores: [], matchStates: [], finalSeed: null, drawn: true });
    state.collapsedRounds = new Set();
    render();
    showToast('Distribuição dos jogos sorteada.');
  } catch (error) {
    button.disabled = false;
    showToast(error.message || 'Não foi possível sortear a distribuição.', true);
  }
}

async function togglePayment(index, button) {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  if (!item || !Supers.individualAthletes(item).some((athlete) => athlete.index === index)) return;
  const payment = Supers.paymentFor(item, index);
  const payments = Array.isArray(item.payments) ? [...item.payments] : [];
  while (payments.length <= index) payments.push(null);
  payments[index] = { amountCents: payment.amountCents, paid: !payment.paid };
  const scroll = window.scrollY;
  button.disabled = true;
  try {
    await saveTournament({ ...item, payments });
    render();
    window.scrollTo(0, scroll);
    showToast(payment.paid ? 'Pagamento marcado como pendente.' : 'Pagamento marcado como recebido.');
  } catch (error) {
    button.disabled = false;
    showToast(error.message || 'Não foi possível atualizar o pagamento.', true);
  }
}

function closeScorePicker(focusTrigger = false) {
  document.querySelector('.score-picker')?.remove();
  const trigger = state.scorePickerTrigger;
  if (trigger?.isConnected) {
    trigger.setAttribute('aria-expanded', 'false');
    if (focusTrigger) trigger.focus();
  }
  state.scorePickerTrigger = null;
}

function openScorePicker(trigger) {
  if (state.scorePickerTrigger === trigger) { closeScorePicker(true); return; }
  closeScorePicker();
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  if (!item || !Super6.validConfig(item.scoring)) return;
  const target = item.scoring.target;
  const maxPreset = Math.min(target, 50);
  const options = Array.from({ length: maxPreset + 1 }, (_, index) => maxPreset - index);
  if (target > 50) options.unshift(target);
  const current = trigger.value === '' ? null : Number(trigger.value);
  const buttons = options.map((value) => `<button class="score-option" type="button" data-action="score-pick" data-value="${value}" aria-pressed="${value === current}">${value}</button>`).join('');
  const extra = target > 50 ? `<div class="score-custom"><label for="score-custom-value">Outro valor</label><div><input id="score-custom-value" type="number" min="0" max="${target}" step="1" inputmode="numeric" /><button type="button" data-action="score-custom-apply">Aplicar</button></div></div>` : '';
  const picker = document.createElement('div');
  picker.className = 'score-picker';
  picker.setAttribute('role', 'dialog');
  picker.setAttribute('aria-label', `Escolha os games da rodada ${Number(trigger.dataset.round) + 1}`);
  picker.innerHTML = `<div class="score-picker-head"><strong>Escolha os games</strong><button type="button" data-action="score-close" aria-label="Fechar seletor">×</button></div><div class="score-options">${buttons}</div>${extra}<button class="score-clear" type="button" data-action="score-clear">Limpar resultado</button>`;
  trigger.parentElement.append(picker);
  const rect = trigger.getBoundingClientRect();
  if (rect.bottom + Math.min(picker.offsetHeight, 280) > window.innerHeight && rect.top > picker.offsetHeight + 10) picker.classList.add('above');
  state.scorePickerTrigger = trigger;
  trigger.setAttribute('aria-expanded', 'true');
  (picker.querySelector('.score-option[aria-pressed="true"]') || picker.querySelector('.score-option')).focus();
}

async function handleScoreSelection(trigger, raw, clearBoth = false) {
  const item = tournaments().find((entry) => entry.id === state.selectedId);
  const data = Supers.format(item?.format);
  if (!data || !Super6.validConfig(item.scoring) || !Supers.athletesReady(item)) return;
  const index = Number(trigger.dataset.round);
  const side = trigger.dataset.scoreSide;
  if (!Number.isInteger(index) || index < 0 || index >= (data.phases ? 20 : data.games.length) || !['left', 'right'].includes(side)) return;
  const previousSeeds = data.phases ? Supers.finalSeeds(item)?.join(',') : null;
  if (data.phases && index >= 10 && !previousSeeds) return;
  const scores = [...(item.scores || [])];
  scores[index] = clearBoth ? { left: null, right: null } : Super6.applyScoreInput(scores[index], side, String(raw), item.scoring);
  const matchStates = Array.isArray(item.matchStates) ? [...item.matchStates] : [];
  const completed = Super6.isComplete(scores[index], item.scoring);
  if (clearBoth) matchStates[index] = null;
  else if (completed && matchStates[index]?.startedAt && !matchStates[index]?.endedAt) {
    matchStates[index] = { ...matchStates[index], endedAt: new Date().toISOString() };
  }
  const next = { ...item, scores, matchStates, ...(data.phases && index >= 10 ? { finalSeed: previousSeeds } : {}) };
  const nextSeeds = data.phases && index < 10 ? Supers.finalSeeds(next)?.join(',') : null;
  if (data.phases && index < 10 && item.finalSeed && nextSeeds && item.finalSeed !== nextSeeds) {
    scores.splice(10);
    matchStates.splice(10);
    next.finalSeed = null;
    showToast('A classificação dos grupos mudou; os resultados da segunda fase foram limpos para evitar atribuí-los a outros atletas.');
  }
  const scroll = window.scrollY;
  try {
    await saveTournament(next);
    render();
    window.scrollTo(0, scroll);
  } catch (error) { showToast(error.message || 'Não foi possível salvar o resultado.', true); }
}

function updateScoringPreview(resetTarget = false) {
  const form = document.querySelector('#scoring-form');
  if (!form) return;
  const mode = form.querySelector('[name="scoring-mode"]:checked').value;
  const targetInput = form.querySelector('#scoring-target');
  if (resetTarget) targetInput.value = mode === 'sum' ? '6' : '7';
  const target = Number(targetInput.value);
  form.querySelectorAll('.scoring-choice').forEach((choice) => choice.classList.toggle('selected', choice.querySelector('input').checked));
  form.querySelector('#scoring-target-label').textContent = mode === 'sum' ? 'Soma de games por jogo' : 'Games para vencer o set';
  const sample = mode === 'sum' ? Math.min(2, target) : Math.min(2, target - 1);
  form.querySelector('#scoring-example').textContent = !Number.isSafeInteger(target) || target <= 0 ? 'Informe um número inteiro maior que zero.' : mode === 'sum' ? `Exemplo: soma ${target} → ${sample} × ${target - sample}.` : `Exemplo: set até ${target} → ${sample} × ${target}.`;
}

app.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-action]');
  if (!button) return;
  const action = button.dataset.action;
  if (action === 'public-tab') { state.publicTab = button.dataset.tab === 'ranking' ? 'ranking' : 'games'; render(); return; }
  if (action === 'share-open') { openShareDialog(); return; }
  if (action === 'admin-credit-open') { openCreditGrantDialog(button.dataset.userId); return; }
  if (action === 'buy-credits') { await startCheckout(button); return; }
  if (action === 'refresh-credits') {
    button.disabled = true;
    try { await loadCreditData(state.user.id); render(true); showToast('Saldo atualizado.'); }
    catch (error) { button.disabled = false; showToast(error.message || 'Não foi possível atualizar o saldo.', true); }
    return;
  }
  if (action === 'draw-tournament') { await drawTournament(button); return; }
  if (action === 'match-open') {
    const index = Number(button.dataset.matchIndex);
    if (Number.isInteger(index) && index >= 0) openMatchDialog(index);
    return;
  }
  if (action === 'payment-toggle') {
    const index = Number(button.dataset.paymentIndex);
    if (Number.isInteger(index) && index >= 0) await togglePayment(index, button);
    return;
  }
  if (action === 'payment-open') {
    const index = Number(button.dataset.paymentIndex);
    if (Number.isInteger(index) && index >= 0) openPaymentDialog(index);
    return;
  }
  if (action === 'round-toggle') {
    const matches = document.getElementById(button.getAttribute('aria-controls'));
    const card = button.closest('.round-card');
    const collapse = button.getAttribute('aria-expanded') === 'true';
    if (!matches || !card) return;
    matches.hidden = collapse;
    card.classList.toggle('collapsed', collapse);
    button.textContent = collapse ? '+' : '−';
    button.setAttribute('aria-expanded', String(!collapse));
    button.setAttribute('aria-label', `${collapse ? 'Expandir' : 'Minimizar'} ${button.dataset.roundLabel}`);
    if (collapse) state.collapsedRounds.add(button.dataset.roundKey);
    else state.collapsedRounds.delete(button.dataset.roundKey);
    return;
  }
  if (action === 'score-open') { openScorePicker(button); return; }
  if (action === 'export-ranking') { await exportRankingImage(button); return; }
  if (action === 'score-close') { closeScorePicker(true); return; }
  if (action === 'score-pick' || action === 'score-clear') {
    const trigger = state.scorePickerTrigger;
    if (trigger) await handleScoreSelection(trigger, button.dataset.value, action === 'score-clear');
    closeScorePicker(true);
    return;
  }
  if (action === 'score-custom-apply') {
    const trigger = state.scorePickerTrigger;
    const value = button.closest('.score-picker').querySelector('#score-custom-value').value;
    const target = tournaments().find((entry) => entry.id === state.selectedId)?.scoring?.target;
    if (!trigger || !Number.isSafeInteger(Number(value)) || value === '' || Number(value) < 0 || Number(value) > target) { showToast('Escolha um número de games válido.', true); return; }
    await handleScoreSelection(trigger, value);
    closeScorePicker(true);
    return;
  }
  if (action === 'auth-mode') {
    if (state.passwordRecovery && button.dataset.mode === 'login') {
      await supabase.auth.signOut();
      clearPasswordRecoveryUrl();
      state.passwordRecovery = false;
      state.authUser = null;
    }
    state.authMode = button.dataset.mode;
    render();
  }
  if (action === 'toggle-deleted') { state.showDeleted = !state.showDeleted; render(); return; }
  if (action === 'delete-tournament') {
    const item = tournaments().find((entry) => entry.id === button.dataset.id);
    if (!item || item.deletedAt || !window.confirm(`Excluir o torneio “${item.name}”?\n\nEle ficará na área Excluídos e poderá ser restaurado depois.`)) return;
    button.disabled = true;
    try {
      await saveTournament({ ...item, deletedAt: new Date().toISOString() });
      state.selectedId = null;
      state.page = 'home';
      state.showDeleted = false;
      render();
      showToast('Torneio movido para Excluídos.');
    } catch (error) { showToast(error.message || 'Não foi possível excluir o torneio.', true); }
    return;
  }
  if (action === 'restore-tournament') {
    const item = tournaments().find((entry) => entry.id === button.dataset.id);
    if (!item || !item.deletedAt) return;
    button.disabled = true;
    try {
      await saveTournament({ ...item, deletedAt: null });
      render();
      showToast('Torneio restaurado com sucesso.');
    } catch (error) { button.disabled = false; showToast(error.message || 'Não foi possível restaurar o torneio.', true); }
    return;
  }
  if (action === 'users' && isAdminSession()) { state.user = state.admin; state.tournamentItems = state.adminTournaments.filter((item) => item.userId === state.admin.id); state.impersonatingId = null; state.selectedId = null; state.showDeleted = false; state.page = 'users'; await loadCreditData(state.admin.id); render(); return; }
  if (action === 'impersonate' && isAdminSession()) {
    const user = state.users.find((entry) => entry.id === button.dataset.userId);
    if (!user) { showToast('Este cadastro não está mais disponível.', true); return; }
    state.user = user;
    state.tournamentItems = state.adminTournaments.filter((item) => item.userId === user.id);
    state.impersonatingId = user.id;
    state.selectedId = null;
    state.showDeleted = false;
    state.page = 'home';
    await loadCreditData(user.id);
    render();
    showToast(`Painel de ${user.name} aberto.`);
    return;
  }
  if (action === 'admin-return' && isAdminSession()) { state.user = state.admin; state.tournamentItems = state.adminTournaments.filter((item) => item.userId === state.admin.id); state.impersonatingId = null; state.selectedId = null; state.showDeleted = false; state.page = 'users'; await loadCreditData(state.admin.id); render(); return; }
  if (action === 'home') { state.page = 'home'; render(); }
  if (action === 'credits') { state.page = 'credits'; try { await loadCreditData(state.user.id); } catch (error) { showToast(error.message || 'Não foi possível carregar os créditos.', true); } render(); return; }
  if (action === 'create') {
    if (!state.authUser) { state.user = null; state.authMode = 'register'; render(); showToast('Crie sua conta para salvar um Super na nuvem.'); return; }
    if (state.creditBalance <= 0) { state.page = isImpersonating() ? 'create' : 'credits'; render(); showToast('Você precisa de 1 crédito para criar um novo Super.', true); return; }
    state.page = 'create'; render();
  }
  if (action === 'detail') { state.selectedId = button.dataset.id; state.page = 'detail'; const item = tournaments().find((entry) => entry.id === state.selectedId); if (!item || item.deletedAt) { state.page = 'home'; render(); return; } state.detailTab = !Super6.validConfig(item?.scoring) ? 'config' : Supers.athletesReady(item) ? 'games' : 'athletes'; render(); }
  if (action === 'detail-tab') { if (button.disabled) return; state.detailTab = button.dataset.tab; render(); }
  if (action === 'logout') { await supabase.auth.signOut(); state.user = null; state.authUser = null; state.admin = null; state.users = []; state.tournamentItems = []; state.adminTournaments = []; state.creditBalance = 0; state.creditTransactions = []; state.impersonatingId = null; state.selectedId = null; state.showDeleted = false; state.page = 'home'; state.authMode = 'login'; render(); }
  if (action === 'toggle-password') {
    const input = document.getElementById(button.dataset.target);
    input.type = input.type === 'password' ? 'text' : 'password';
    button.setAttribute('aria-label', input.type === 'password' ? 'Mostrar senha' : 'Ocultar senha');
  }
});
app.addEventListener('keydown', (event) => {
  if (!['Enter', ' '].includes(event.key)) return;
  const card = event.target.closest('.match-card[data-action="match-open"]');
  if (!card || event.target !== card) return;
  event.preventDefault();
  const index = Number(card.dataset.matchIndex);
  if (Number.isInteger(index) && index >= 0) openMatchDialog(index);
});
app.addEventListener('change', (event) => {
  if (event.target.name === 'scoring-mode') updateScoringPreview(true);
});
app.addEventListener('input', (event) => {
  if (event.target.id === 'scoring-target') updateScoringPreview();
});
document.addEventListener('click', (event) => {
  if (state.scorePickerTrigger && !event.target.closest('.score-picker, .score-trigger')) closeScorePicker();
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && state.scorePickerTrigger) { event.preventDefault(); closeScorePicker(true); }
});
app.addEventListener('submit', async (event) => {
  event.preventDefault();
  if (event.target.id === 'auth-form') await handleAuth(event.target);
  if (event.target.id === 'password-recovery-request-form') await handlePasswordRecoveryRequest(event.target);
  if (event.target.id === 'password-reset-form') await handlePasswordReset(event.target);
  if (event.target.id === 'create-form') await handleCreate(event.target);
  if (event.target.id === 'scoring-form') await handleScoring(event.target);
  if (event.target.id === 'athletes-form') await handleAthletes(event.target);
});

window.addEventListener('hashchange', () => window.location.reload());
supabase?.auth.onAuthStateChange((event, session) => {
  if (event !== 'PASSWORD_RECOVERY') return;
  state.passwordRecovery = true;
  state.authMode = 'reset';
  state.user = null;
  state.authUser = session?.user || null;
  state.loading = false;
  render();
});
initializeApp();
render();
window.setInterval(updateMatchTimers, 1000);
